import React from "react";
import BaseComponent from "../BaseComponent.js";
import styled from "styled-components";
import searchRequestService from "../../services/SearchService";
import cardService from "../../services/CardService";
import nota from "../Global/nota.guidelines.json";
import { downloadBlob, getByPath } from "./../Global/tools.js";
import { ScrollPercentage } from "./ScrollPercentage.js";

import NanoFlex from "./../Global/NanoFlex";
import Type from "./../Global/Typography.js";
import SearchListBlock from "./SearchListBlock.js";
import Loading from "./../Nano/Loading.js";
import Spinner from "./../Nano/Spinner.js";
import Modal from "./../Global/Modal.js";

import NavSearchList from "../Micro/NavSearchList.js";
import HeaderTableSearchList from "../Micro/HeaderTableSearchList.js";
import TableSearchList from "../Macro/TableSearchList.js";
import PlaylistModal from "../Micro/PlaylistModal.js";
import RemoveFromPlaylistModal from "../Micro/RemoveFromPlaylistModal.js";
import RecapFiltersModal from "../Micro/RecapFiltersModal.js";
import ModalContent from "../Micro/ModalContent.js";

import { ReactComponent as NoData } from "./../Global/Icons/noData.svg";
import { ReactComponent as ArrowUp } from "./../Global/Icons/arrowUp.svg";

const multiple = nota.global.multiple;

const StyledSearchContainer = styled(NanoFlex)`
  width: 100%;
  padding: ${multiple * 2 + "px"}; /* 16px */
  position: relative;
  .scrollPercentage {
    width: 100%;
  }

  .thumbsContainer,
  .listContainer {
    width: 100%;
  }
  /* SCROLL TOP BTN */
  .scrollTopBtnContainer {
    position: fixed;
    bottom: 80px;
    right: 40px;
    height: auto;
    width: auto;

    .scrollTopBtn {
      background-color: ${nota.color.main.default};
      width: 40px;
      height: 40px;
      border-radius: 100%;

      cursor: pointer;
      svg {
        width: 16px;
        height: auto;
      }
    }
  }
  .showScrollTop {
    animation: slide-in-blurred-top 1.6s cubic-bezier(0.23, 1, 0.32, 1);
    display: flex;
  }

  .hideScrollTop {
    display: none;
    transition: display 1s ease-in-out;
  }

  .isDesktop {
    bottom: 56px;
    right: 20px;
    .scrollTopBtn {
      width: 24px;
      height: 24px;
      svg {
        width: 12px;
      }
    }
  }

  .noResultsWithRecap {
    height: calc(100vh - 207px);
  }

  /* ANIMATION */
  @keyframes slide-in-blurred-top {
    0% {
      transform: translateY(1000px) scaleY(2.5) scaleX(0.2);
      transform-origin: 50% 0%;
      filter: blur(40px);
      opacity: 0;
    }
    100% {
      transform: translateY(0) scaleY(1) scaleX(1);
      transform-origin: 50% 50%;
      filter: blur(0);
      opacity: 1;
    }
  }
`;

const StyledLoading = styled(NanoFlex)`
  height: calc(100vh - 130px);
`;

const StyledNoData = styled(NanoFlex)`
  height: calc(100vh - 130px);
  .iconWrapper {
    height: auto;
    margin-bottom: 24px;
    svg {
      height: auto;
      max-width: 344px;
      width: 60%;
    }
  }
  .infoWrapper {
    max-width: 80%;
    height: auto;
    margin-bottom: 8px;
    text-align: center;
    a {
      display: inline-block;
      text-decoration: none;
      margin-left: 4px;
      p {
        color: ${nota.color.main.default};
      }
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const StyledSearchResult = styled.div`
  margin-left: ${multiple / 2 + "px"}; /* 4px */
`;

class SearchResult extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = this.getDefaultState();
    this.isGettingData = false;
    this.requestId = 0;
    this.currentVisibleIndex = 0;
  }

  getDefaultState = () => {
    return {
      isLoading: true,
      filter: this.props.filter,
      count: 0,
      hideRatings: false,
      data: [],
      hasAllData: false,
      options: this.getDefaultOptions(),
      selectedMenu: this._isMobile || this._isTablet ? "thumbs" : localStorage.getItem("selectedMenu") ?? "thumbs",
      openPlaylistModal: false,
      openRemovePlaylistModal: false,
      recapFilters: [],
      openRecapFilters: true,
      openRecapFiltersModal: false,
      hideSelection: true,
      showScrollTop: false,
      pdfMaxExceeded: false,
      excelMaxExceeded: false,
    };
  };

  getDefaultOptions = () => {
    return {
      page: 0,
      resultsPerPage: 120,
      isAllSelected: true,
      selectMode: "Exclude",
      selectedIds: [],
      orderBy: [],
    };
  };

  componentDidMount() {
    super.componentDidMount();
    window.addEventListener("languageChange", this.onSearchResultLanguageChange, false);
    window.addEventListener("isMobileChange", this.onIsMobileChange, false);
    window.addEventListener("isTabletChange", this.onIsTabletChange, false);
    window.addEventListener("advancedSearchVisibleChanged", this.advancedSearchVisibleChanged, false);

    this.getData(true);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.filter !== prevState.filter) {
      let defaultState = this.getDefaultState();
      defaultState.filter = this.state.filter;
      //Keep selected menu and order by
      defaultState.selectedMenu = this.state.selectedMenu;
      defaultState.options.orderBy = this.state.options.orderBy;
      this.setState(defaultState, () => {
        this.getData(true);
      });
    }

    if (JSON.stringify(this.props.filter) !== JSON.stringify(prevProps.filter)) {
      this.setState({ filter: this.props.filter });
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    window.removeEventListener("languageChange", this.onSearchResultLanguageChange, false);
    window.removeEventListener("isMobileChange", this.onIsMobileChange, false);
    window.removeEventListener("isTabletChange", this.onIsTabletChange, false);
    window.removeEventListener("advancedSearchVisibleChanged", this.advancedSearchVisibleChanged, false);
  }

  onSearchResultLanguageChange = () => {
    if (this.props.isSearchInvalid === false) {
      this.isGettingData = true;
      this.requestId++;
      let currentRequestId = this.requestId;
      this.setState({ isLoading: true }, () => {
        let options = { ...this.state.options };
        options.page = 1;
        if (this.state.data.length > 0) {
          options.resultsPerPage = this.state.data.length;
        }
        searchRequestService
          .getAdvancedSearchPrograms({ ...this.state.filter, ...options })
          .then((response) => {
            if (response.ok) {
              return response.json();
            } else {
              // Catch
              if ((response.status === 403 || response.status === 401)) window.dispatchEvent(new Event("sendToLogin"));
            }
          })
          .then((data) => {
            this.getRecapFilters();
            if (this.requestId === currentRequestId) {
              let count = data.count;
              this.setState(
                {
                  isLoading: false,
                  isSpinnerOn: false,
                  data: data.programs,
                  count: count,
                  hasAllData: data.programs.length === count,
                },
                () => {
                  this.isGettingData = false;
                }
              );
            }
          });
      });
    }
  };

  onIsMobileChange = () => {
    if (this._isMobile && this.state.selectedMenu === "list") {
      this.setState({ selectedMenu: "thumbs" });
    }
  };

  onIsTabletChange = () => {
    if (this._isTablet && this.state.selectedMenu === "list") {
      this.setState({ selectedMenu: "thumbs" });
    }
  };

  onScroll = (percentage, entry) => {
    if (document.documentElement.scrollTop > 250 && !this.state.isLoading) {
      if (!this.state.showScrollTop) {
        this.setState({ showScrollTop: true });
      }
    } else {
      if (this.state.showScrollTop) {
        this.setState({ showScrollTop: false });
      }
    }

    let perc = percentage * 100;
    if (perc > 65 && !this.isGettingData) {
      this.getData();
    }
  };

  advancedSearchVisibleChanged = () => {
    this.forceUpdate();
  };

  getData = (firstLoad) => {
    if (this.props.isSearchInvalid === false) {
      if (!this.state.hasAllData) {
        if (firstLoad) this.getRecapFilters();
        this.isGettingData = true;
        this.requestId++;
        let currentRequestId = this.requestId;
        this.setState({ isSpinnerOn: true }, () => {
          let options = { ...this.state.options };
          options.page++;
          if (options.page === 1 && window.scrollY !== 0) {
            this.scrollTop();
          }
          if (firstLoad) options.ignoreCount = false;
          searchRequestService
            .getAdvancedSearchPrograms({ ...this.state.filter, ...options })
            .then((response) => {
              if (response.ok) {
                return response.json();
              } else {
                // Catch
                if ((response.status === 403 || response.status === 401)) window.dispatchEvent(new Event("sendToLogin"));
              }
            })
            .then((data) => {
              if (!data) return;

              if (this.requestId === currentRequestId) {
                let newData = [...this.state.data, ...data.programs];
                if (firstLoad) options.ignoreCount = true;
                let count = firstLoad ? data.count : this.state.count;
                this.setState(
                  {
                    isLoading: false,
                    isSpinnerOn: false,
                    data: newData,
                    count: count,
                    hideRatings: this.state.hideRatings == false && data.hideRatings == false ? false : true,
                    hasAllData: newData.length === count,
                    options: options,
                  },
                  () => {
                    this.isGettingData = false;
                  }
                );
              }
            });
        });
      }
    }
  };

  getRecapFilters = () => {
    window.dispatchEvent(
      new CustomEvent("getRecapFilters", {
        detail: {
          callback: (data) => {
            this.setState({
              recapFilters: data,
              openRecapFilters: data.length > 0,
            });
          },
        },
      })
    );
  };

  splitArray = (arr, size) => {
    var myArray = [];
    for (var i = 0; i < arr.length; i += size) {
      myArray.push(arr.slice(i, i + size));
    }
    return myArray;
  };

  onSearchListBlockVisibleChange = (index, isVisible) => {
    if (isVisible) {
      this.currentVisibleIndex = index;
    }
  };

  onMenuChange = (menu) => {
    localStorage.setItem("selectedMenu", menu);
    this.setState({ selectedMenu: menu });
  };

  onOrderByChange = (orderBy) => {
    let options = { ...this.state.options };
    options.page = 0;
    options.orderBy = orderBy;
    this.setState({ isLoading: true, data: [], hasAllData: false, options: options }, () => {
      this.getData();
    });
  };

  onIsAllSelectedChange = (isAllSelected) => {
    let options = { ...this.state.options };
    options.isAllSelected = isAllSelected;
    options.selectMode = isAllSelected ? "Exclude" : "Include";
    options.selectedIds = [];
    this.setState({ options: options });
  };

  onSelectedChange = (idProgram) => {
    let options = { ...this.state.options };
    let selectedId = options.selectedIds.find((v) => v === idProgram);
    if (selectedId) {
      options.selectedIds = options.selectedIds.filter((v) => v !== idProgram);
    } else {
      options.selectedIds.push(idProgram);
    }
    options.isAllSelected = this.getSelectedCount(options.selectedIds) == this.state.count;
    this.setState({ options: options });
  };

  getSelectedCount = (selectedIds) => {
    if (!selectedIds) selectedIds = this.state.options.selectedIds;
    let selectedCount = 0;
    switch (this.state.options.selectMode) {
      case "Exclude":
        selectedCount = this.state.count - selectedIds.length;
        break;
      case "Include":
        selectedCount = selectedIds.length;
        break;
    }
    return selectedCount;
  };

  getCardReport = () => {
    if (this.getSelectedCount() <= 50) {
      this.setState({ isLoading: true }, () => {
        cardService
          .getCardPdf({ ...this.state.filter, ...this.state.options })
          .then((response) => {
            if (response.ok) {
              return response.blob();
            } else {
              // Catch
              if ((response.status === 403 || response.status === 401)) window.dispatchEvent(new Event("sendToLogin"));
            }
            return null;
          })
          .then((blob) => {
            if (blob) {
              downloadBlob(blob, `Programme-Page.pdf`);
            }
            this.setState({ isLoading: false });
          });
      });
    } else {
      this.setState({ pdfMaxExceeded: true });
    }
  };

  getExport = () => {
    if (this.getSelectedCount() <= 30000) {
      this.setState({ isLoading: true }, () => {
        searchRequestService
          .getAdvancedSearchProgramsExport({ ...this.state.filter, ...this.state.options, ...{ recapFilters: this.getTranslatedRecapFilters() } })
          .then((response) => {
            if (response.ok) {
              return response.blob();
            } else {
              // Catch
              if ((response.status === 403 || response.status === 401)) window.dispatchEvent(new Event("sendToLogin"));
            }
            return null;
          })
          .then((blob) => {
            if (blob) {
              downloadBlob(blob, `Programme-Page-Report.xlsx`);
            }
            this.setState({ isLoading: false });
          });
      });
    } else {
      this.setState({ excelMaxExceeded: true });
    }
  };

  onRecapFiltersClick = () => {
    this.setState({ openRecapFilters: !this.state.openRecapFilters });
  };

  openRecapFiltersModal = () => {
    this.setState({ openRecapFiltersModal: true });
  };

  getTranslatedRecapFilters = () => {
    let recapFilters = JSON.parse(JSON.stringify(this.state.recapFilters));

    recapFilters.forEach((item) => {
      item.translation = getByPath(item.translation, this.translation);
    });

    return recapFilters;
  };

  onToggleHideSelection = () => {
    this.setState({ hideSelection: !this.state.hideSelection });
  };

  scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  render() {
    if (this.props.isSearchInvalid) {
      return (
        <StyledNoData>
          <NanoFlex flexDirection="column">
            <NanoFlex className="iconWrapper">
              <NoData />
            </NanoFlex>
            <NanoFlex className="infoWrapper">
              <Type.p>{this.translation.components.searchResult.invalidSearchMessage.title}</Type.p>
            </NanoFlex>
            <NanoFlex className="infoWrapper" alignContent>
              <Type.p>
                {this.translation.components.searchResult.invalidSearchMessage.subTitle}
                <a href="mailto:e-nota@glance-mediametrie.com">
                  <Type.p> {this.translation.geral.notaTeam}</Type.p>
                </a>
              </Type.p>
            </NanoFlex>
          </NanoFlex>
        </StyledNoData>
      );
    }

    let advancedSearchVisible = JSON.parse(sessionStorage.getItem("advancedSearchVisible"));
    return (
      <StyledSearchContainer>
        <ScrollPercentage as="div" onChange={this.onScroll}>
          <NanoFlex flexDirection="column" id={"scrollViewPort"}>
            {this.state.isLoading && (
              <StyledLoading className="loadingContainer">
                <Loading />
              </StyledLoading>
            )}
            {!this.state.isLoading && (
              <NanoFlex flexDirection={"column"} justifyContent={"flex-start"} alignItems={"flex-start"}>
                <NavSearchList
                  count={this.state.count}
                  selectedMenu={this.state.selectedMenu}
                  onMenuChange={this.onMenuChange}
                  selectedCount={this.getSelectedCount()}
                  onOpenPlaylistModal={() => {
                    this.setState({ openPlaylistModal: true });
                  }}
                  onOpenRemovePlaylistModal={() => {
                    this.setState({ openRemovePlaylistModal: true });
                  }}
                  onPdfClick={() => {
                    this.getCardReport();
                  }}
                  onExcelClick={() => {
                    this.getExport();
                  }}
                  isAllSelected={this.state.options.isAllSelected}
                  onIsAllSelectedChange={this.onIsAllSelectedChange}
                  onRecapFiltersClick={this.onRecapFiltersClick}
                  openRecapFilters={this.state.openRecapFilters}
                  recapFilters={this.getTranslatedRecapFilters()}
                  recapFiltersVisible={this.state.recapFilters.length > 0}
                  openRecapFiltersModal={this.openRecapFiltersModal}
                  hideSelection={this.state.hideSelection}
                  onToggleHideSelection={this.onToggleHideSelection}
                  advancedSearchVisible={advancedSearchVisible}
                />
                {this.state.count === 0 && (
                  <StyledNoData className="noResultsWithRecap">
                    <NanoFlex flexDirection="column">
                      <NanoFlex className="iconWrapper">
                        <NoData />
                      </NanoFlex>
                      <NanoFlex className="infoWrapper">
                        <Type.p>{this.translation.components.searchResult.noDataMessage.title}</Type.p>
                      </NanoFlex>
                      <NanoFlex className="infoWrapper" alignContent>
                        <Type.p>
                          {this.translation.components.searchResult.noDataMessage.subTitle}
                          <a href="mailto:e-nota@glance-mediametrie.com">
                            <Type.p> {this.translation.geral.notaTeam}</Type.p>
                          </a>
                        </Type.p>
                      </NanoFlex>
                    </NanoFlex>
                  </StyledNoData>
                )}
                {this.state.count > 0 && this.state.selectedMenu === "thumbs" && (
                  <div className="thumbsContainer" style={{ width: "100%" }}>
                    {this.splitArray(this.state.data, this.state.options.resultsPerPage).map((data, dataIndex) => (
                      <SearchListBlock
                        key={dataIndex}
                        index={dataIndex}
                        data={data}
                        selectable={true}
                        selectedIds={this.state.options.selectedIds}
                        selectMode={this.state.options.selectMode}
                        onSearchListBlockVisibleChange={this.onSearchListBlockVisibleChange}
                        onSelectedChange={(item) => {
                          this.onSelectedChange(item.idProgram);
                        }}
                        hasBiggerThumb={true}
                        hideSelection={this.state.hideSelection}
                      />
                    ))}
                  </div>
                )}
                {this.state.count > 0 && this.state.selectedMenu === "list" && (
                  <div className="listContainer">
                    <HeaderTableSearchList
                      hideRatings={this.state.hideRatings}
                      orderBy={this.state.options.orderBy}
                      onOrderByChange={this.onOrderByChange}
                      isAllSelected={this.state.options.isAllSelected}
                      onIsAllSelectedChange={this.onIsAllSelectedChange}
                      openRecapFilters={this.state.openRecapFilters}
                      advancedSearchVisible={advancedSearchVisible}
                    />
                    {this.splitArray(this.state.data, 50).map((data, dataIndex) => (
                      <TableSearchList
                        key={dataIndex}
                        data={data}
                        hideRatings={this.state.hideRatings}
                        isAllSelected={this.state.options.isAllSelected}
                        onSelectedChange={this.onSelectedChange}
                        selectedIds={this.state.options.selectedIds}
                        selectMode={this.state.options.selectMode}
                        advancedSearchVisible={advancedSearchVisible}
                      />
                    ))}
                  </div>
                )}
              </NanoFlex>
            )}

            <NanoFlex className={`scrollTopBtnContainer ${this.state.showScrollTop ? "showScrollTop" : "hideScrollTop"} ${!this._isMobile ? "isDesktop" : ""}`} onClick={this.scrollTop}>
              <NanoFlex className="scrollTopBtn">
                <ArrowUp />
              </NanoFlex>
            </NanoFlex>

            {!this.state.isLoading && this.state.isSpinnerOn && (
              <NanoFlex height={"100px"}>
                <Spinner />
              </NanoFlex>
            )}
            <Modal show={this.state.openPlaylistModal} content={<PlaylistModal filter={{ ...this.state.filter, ...this.state.options }} mode={"Add"} cancelAction={() => this.setState({ openPlaylistModal: false })} nextAction={() => this.setState({ openPlaylistModal: false })} />} />
            <Modal
              show={this.state.openRemovePlaylistModal}
              content={
                <RemoveFromPlaylistModal
                  count={this.getSelectedCount()}
                  filter={{ ...this.state.filter, ...this.state.options }}
                  cancelAction={() => this.setState({ openRemovePlaylistModal: false })}
                  nextAction={() => {
                    this.setState({ openRemovePlaylistModal: false }, () => {
                      let defaultState = this.getDefaultState();
                      defaultState.filter = this.state.filter;
                      //Keep selected menu and order by
                      defaultState.selectedMenu = this.state.selectedMenu;
                      defaultState.options.orderBy = this.state.options.orderBy;
                      this.setState(defaultState, () => {
                        this.getData(true);
                      });
                    });
                  }}
                />
              }
            />

            <Modal
              show={this.state.pdfMaxExceeded}
              content={
                <ModalContent
                  message={"Please, PDF export is limited up to 50 programmes. Try other criteria or contact the NoTa Team"}
                  actions={[
                    {
                      label: this.translation.geral.continue,
                      action: () => {
                        this.setState({ pdfMaxExceeded: false });
                      },
                      simple: false,
                    },
                  ]}
                />
              }
            />

            <Modal
              show={this.state.excelMaxExceeded}
              content={
                <ModalContent
                  message={"Please, Excel export is limited up to 30,000 programmes. Try other criteria or contact the NoTa Team"}
                  actions={[
                    {
                      label: this.translation.geral.continue,
                      action: () => {
                        this.setState({ excelMaxExceeded: false });
                      },
                      simple: false,
                    },
                  ]}
                />
              }
            />

            <Modal show={this.state.openRecapFiltersModal} content={<RecapFiltersModal recapFilters={this.state.recapFilters} cancelAction={() => this.setState({ openRecapFiltersModal: false })} nextAction={() => this.setState({ openRecapFiltersModal: false })} />} />
          </NanoFlex>
        </ScrollPercentage>
      </StyledSearchContainer>
    );
  }
}

SearchResult.defaultProps = {};

export default SearchResult;
